import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';
import { partnersData } from '@/mocks/home';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('company_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('company_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u20hf8l97n3eas3fr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      const code = typeof parsed?.code === 'string' ? parsed.code : '';
      const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.meta as Record<string, string>)?.detail || '';

      if (res.ok && code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(msg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Organization" />
      <SEOSchema
        type="WebPage"
        data={{
          name: '关于云开体育 - 专业体育服务平台',
          description: '云开体育品牌介绍、信誉展示及联系方式。了解云开体育的发展历程、核心价值和合作伙伴。',
        }}
      />

      <Navbar />
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '关于我们' },
        ]}
      />

      <main>
        {/* Brand Introduction */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h4 className="text-primary-600 font-semibold text-sm mb-2">关于云开体育</h4>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
                  让运动点亮生活
                </h1>
                <div className="text-foreground-700 text-base leading-relaxed space-y-4">
                  <p>
                    云开体育成立于2018年，是国内领先的专业体育服务综合平台。我们以<strong>「让更多人享受运动乐趣」</strong>为使命，致力于通过科技赋能体育产业，打造一站式运动服务生态。
                  </p>
                  <p>
                    经过多年发展，云开体育已汇聚<strong>500多名专业教练</strong>，合作<strong>200多家优质场馆</strong>，服务超过<strong>50,000名活跃会员</strong>。我们的业务涵盖体育培训、赛事组织、运动健身、场馆预约和青少年训练等多个领域，覆盖全国30多个主要城市。
                  </p>
                  <p>
                    云开体育坚信运动的力量——它不仅能强健体魄，更能塑造品格、凝聚团队、传递正能量。我们持续投入教学研发和技术创新，为用户提供更专业、更便捷、更有温度的体育服务体验。
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-6 mt-8">
                  <div className="text-center">
                    <div className="font-heading text-3xl font-bold text-primary-600">2018</div>
                    <div className="text-foreground-500 text-sm">创立年份</div>
                  </div>
                  <div className="text-center">
                    <div className="font-heading text-3xl font-bold text-primary-600">500+</div>
                    <div className="text-foreground-500 text-sm">专业教练</div>
                  </div>
                  <div className="text-center">
                    <div className="font-heading text-3xl font-bold text-primary-600">30+</div>
                    <div className="text-foreground-500 text-sm">覆盖城市</div>
                  </div>
                </div>
              </div>

              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20bright%20sports%20company%20office%20with%20team%20meeting%2C%20professional%20staff%20discussing%20sports%20programs%2C%20warm%20natural%20light%20through%20large%20windows%2C%20corporate%20lifestyle%20photography%2C%20clean%20minimal%20aesthetic&width=700&height=500&seq=about-brand&orientation=landscape"
                  alt="云开体育团队"
                  className="w-full rounded-xl object-cover"
                  style={{ height: '420px' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl font-bold text-foreground-900 mb-3">核心价值观</h2>
              <p className="text-foreground-600 text-base">我们的信念，驱动我们不断前行</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-heart-pulse-line', title: '专业至上', desc: '汇聚行业顶尖人才，以专业的教学和服务品质赢得信赖' },
                { icon: 'ri-team-line', title: '以人为本', desc: '关注每一位用户的运动需求和体验，用心服务每一个细节' },
                { icon: 'ri-lightbulb-line', title: '创新驱动', desc: '持续投入技术研发，用科技手段提升运动服务的效率和品质' },
                { icon: 'ri-earth-line', title: '社会责任', desc: '积极推动全民健身事业，用运动的力量创造积极的社会价值' },
              ].map((v) => (
                <div key={v.title} className="bg-background-50 rounded-xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${v.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partners */}
        <section id="partners" className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl font-bold text-foreground-900 mb-3">合作伙伴</h2>
              <p className="text-foreground-600 text-base">与行业领先机构携手，共建体育服务生态</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {partnersData.map((partner) => (
                <div
                  key={partner}
                  className="bg-background-100 rounded-xl px-4 py-6 flex items-center justify-center border border-background-200/70"
                >
                  <span className="font-heading text-base font-semibold text-foreground-700 text-center">{partner}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl font-bold text-foreground-900 mb-3">联系我们</h2>
              <p className="text-foreground-600 text-base">如有任何问题或合作意向，欢迎与我们联系</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {[
                { icon: 'ri-phone-line', label: '客服电话', value: '400-888-0000' },
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@yunkai-sports.com' },
                { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区奥林匹克公园' },
              ].map((item) => (
                <div key={item.label} className="bg-background-50 rounded-xl p-5 text-center border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-lg text-primary-600`}></i>
                  </div>
                  <div className="text-foreground-500 text-xs mb-1">{item.label}</div>
                  <div className="text-foreground-900 font-medium text-sm">{item.value}</div>
                </div>
              ))}
            </div>

            <form
              className="bg-background-50 rounded-xl p-6 border border-background-200/70"
              data-readdy-form=""
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                  placeholder="请输入咨询主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容 *</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容（不超过500字）"
                ></textarea>
              </div>

              <div className="honeypot-field">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              {formStatus === 'success' && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-secondary-100 text-secondary-800 text-sm">
                  <i className="ri-check-line mr-1.5"></i> 提交成功！感谢您的留言，我们会尽快与您联系。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-primary-100 text-primary-800 text-sm">
                  <i className="ri-error-warning-line mr-1.5"></i> {formError}
                </div>
              )}

              <button
                type="submit"
                className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                提交留言
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}